import { Color, Euler, Vector3 } from "three";

import SingleOpdek from "./door-configurations/single-opdek";
import SingleOpdekKozijn from "./door-configurations/single-opdek-kozijn";
import SingleSchuif from "./door-configurations/single-schuif";
import SingleTaats from "./door-configurations/single-taats";
import SinglePaneel from "./door-configurations/single-paneel";
import SingleScharnier from "./door-configurations/single-scharnier";
import DoublePaneel from "./door-configurations/double-paneel";
import SingleStompe from "./door-configurations/single-stompe";
import TriplePanel from "./door-configurations/triple-panel";
import QuadruplePanel from "./door-configurations/quadruple-panel";
import DoubleTaats from "./door-configurations/double-taats";
import DoubleTaatsSinglePanel from "./door-configurations/double-taats-single-panel";
import DoubleTaatsDoublePanel from "./door-configurations/double-taats-double-panel";
import SingleTaatsDoublePanel from "./door-configurations/single-taats-double-panel";
import SinglePanelSingleTaatsSinglePanel from "./door-configurations/single-panel-single-taats-single-panel";
import SingleTaatsSinglePanel from "./door-configurations/single-taats-single-panel";
import SingleSchuifDoublePanel from "./door-configurations/single-schuif-double-panel";
import SingleSchuifDoublePanelSingleSchuif from "./door-configurations/single-schuif-double-panel-single-schuif";
import DoubleSchuifDoublePanel from "./door-configurations/double-schuif-double-panel";
import SingleSchuifSinglePanelSingleSchuif from "./door-configurations/single-schuif-single-panel-single-schuif";
import SingleSchuifSinglePanelSingleSchuifStairs from "./door-configurations/single-schuif-single-panel-single-schuif-stairs";
import DoubleSchuifSinglePanel from "./door-configurations/double-schuif-single-panel";
import SinglePanelDoubleTaatsSinglePanel from "./door-configurations/single-panel-double-taats-single-panel";
import SinglePanelDoubleSchuifSinglePanel from "./door-configurations/single-panel-double-schuif-single-panel";
import DoubleSchuif from "./door-configurations/double-schuif";
import SinglePanelSingleSchuifSinglePanel from "./door-configurations/single-panel-single-schuif-single-panel";
import SingleSchuifSinglePanel from "./door-configurations/single-schuif-single-panel";
import SingleScharnierSinglePanel from "./door-configurations/single-scharnier-single-panel";
import DoubleScharnier from "./door-configurations/double-scharnier";
import DoubleScharnierSinglePanel from "./door-configurations/double-scharnier-single-panel";
import DoubleScharnierDoublePanel from "./door-configurations/double-scharnier-double-panel";
import SinglePanelSingleScharnierSinglePanel from "./door-configurations/single-panel-single-scharnier-single-panel";
import SingleScharnierSinglePanelSingleScharnier from "./door-configurations/single-scharnier-single-panel-single-scharnier";
import SingleScharnierSinglePanelSingleScharnierStairs from "./door-configurations/single-scharnier-single-panel-single-scharnier-stairs";
import SingleScharnierDoublePanel from "./door-configurations/single-scharnier-double-panel";
import SingleScharnierDoublePanelSingleScharnier from "./door-configurations/single-scharnier-double-panel-single-scharnier";
import DoubleScharnierDoublePanelLeft from "./door-configurations/double-scharnier-double-panel-left";
import { useGLTF } from "@react-three/drei";
import {
  DoorGlassMaterial,
  GLTFMaterialResult,
  adjustNewColors,
} from "shared/utils/door";
import {
  DoorSettings,
  RailSettings,
  frameSettings,
  kozijnSettings,
  lockSettings,
  useDoorSettings,
} from "shared/utils/doorConfigurations";
import { useEffect } from "react";
import { getFullAssetPath } from "shared/utils/assets";
import { singleDoubleDoorShiftValue } from "shared/utils/doorSceneConstants";

const zeroRotation = new Euler(0, 0, 0);
interface DoorProps {
  door: string | undefined;
  position: Vector3;
  scale: Vector3;
}

export interface ConfigurationProps {
  materials: DoorGlassMaterial;
  position: Vector3;
  rotation?: Euler;
  scale?: Vector3;
  doorSettings?: DoorSettings;
}

export interface DoorComponentProps {
  materials: DoorGlassMaterial;
  position: Vector3;
  rotation?: Euler;
  index?: number;
  frameSettings?: frameSettings;
  kozijnSettings?: kozijnSettings;
  railSettings?: RailSettings;
  lockSettings?: lockSettings;
  rotateNewlayouts?: boolean;
}

const Door = ({ door, position, scale }: DoorProps) => {
  const { materials } = useGLTF(
    getFullAssetPath("/objects-v3/materialsCompressedv4.glb"),
  ) as GLTFMaterialResult;

  // Adjust colors
  // HARDCODED
  useEffect(() => {
    adjustNewColors(materials);
  }, [materials]);

  const { getDoorSettings } = useDoorSettings();

  const doorSettings = getDoorSettings(door);

  const rotate = new Euler(0, Math.PI, 0);

  const doorsRecord: Record<string, JSX.Element> = {
    opdekdeurmetkozijn: (
      <SingleOpdekKozijn
        materials={materials}
        position={position}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    opdekdeur: (
      <SingleOpdek
        materials={materials}
        position={position}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    stompedeur: (
      <SingleStompe
        materials={materials}
        position={position}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    schuifdeur: (
      <SingleSchuif
        materials={materials}
        position={position}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    taatsdeur: (
      <SingleTaats
        materials={materials}
        position={position}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    paneel: (
      <SinglePaneel
        materials={materials}
        position={position}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    scharnierdeur: (
      <SingleScharnier
        materials={materials}
        position={position}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    paneelpaneel: (
      <DoublePaneel
        materials={materials}
        position={position}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    paneelpaneelpaneel: (
      <TriplePanel
        materials={materials}
        position={position}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    paneelpaneelpaneelpaneel: (
      <QuadruplePanel
        materials={materials}
        position={position}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    dubbel: (
      <DoubleTaats
        materials={materials}
        position={position}
        rotation={rotate}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    dubbel1paneel: (
      <DoubleTaatsSinglePanel
        materials={materials}
        position={position}
        rotation={rotate}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    dubbel2paneel: (
      <SinglePanelDoubleTaatsSinglePanel
        materials={materials}
        position={position}
        rotation={rotate}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    taatsdeurtaatsdeurpaneelpaneel: (
      <DoubleTaatsDoublePanel
        materials={materials}
        position={position}
        rotation={rotate}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    taatsdeurpaneelpaneel: (
      <SingleTaatsDoublePanel
        materials={materials}
        position={position}
        rotation={rotate}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    taatsdeur2paneel: (
      <SinglePanelSingleTaatsSinglePanel
        materials={materials}
        position={position}
        rotation={rotate}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    taatsdeur1paneel: (
      <SingleTaatsSinglePanel
        materials={materials}
        position={position}
        rotation={rotate}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    schuifdeurpaneelpaneel: (
      <SingleSchuifDoublePanel
        materials={materials}
        position={position}
        rotation={rotate}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    schuifdeurpaneelpaneelschuifdeur: (
      <SingleSchuifDoublePanelSingleSchuif
        materials={materials}
        position={position}
        rotation={rotate}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    schuifdeurschuifdeurpaneelpaneel: (
      <DoubleSchuifDoublePanel
        materials={materials}
        position={position}
        rotation={rotate}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    schuifdeurpaneelschuifdeur: (
      <SingleSchuifSinglePanelSingleSchuif
        materials={materials}
        position={position}
        rotation={rotate}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    dubbeleschuifdeur1paneel: (
      <DoubleSchuifSinglePanel
        materials={materials}
        position={position}
        rotation={rotate}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    dubbeleschuifdeur2paneel: (
      <SinglePanelDoubleSchuifSinglePanel
        materials={materials}
        position={position}
        rotation={rotate}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    dubbeleschuifdeur: (
      <DoubleSchuif
        materials={materials}
        position={position}
        rotation={rotate}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    schuifdeur2paneel: (
      <SinglePanelSingleSchuifSinglePanel
        materials={materials}
        position={position}
        rotation={rotate}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    schuifdeur1paneel: (
      <SingleSchuifSinglePanel
        materials={materials}
        position={position}
        rotation={rotate}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    scharnierdeur1paneel: (
      <SingleScharnierSinglePanel
        materials={materials}
        position={position}
        rotation={zeroRotation}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    scharnierdeur2paneel: (
      <SinglePanelSingleScharnierSinglePanel
        materials={materials}
        position={position}
        rotation={rotate}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    dubbelescharnierdeur: (
      <DoubleScharnier
        materials={materials}
        position={position}
        rotation={rotate}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    dubbelescharnierdeur1paneel: (
      <DoubleScharnierSinglePanel
        materials={materials}
        position={position}
        rotation={rotate}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    dubbelescharnierdeur2paneel: (
      <DoubleScharnierDoublePanel
        materials={materials}
        position={position}
        rotation={rotate}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    scharnierdeurpaneelscharnierdeur: (
      <SingleScharnierSinglePanelSingleScharnier
        materials={materials}
        position={position}
        rotation={rotate}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    scharnierdeurpaneelpaneel: (
      <SingleScharnierDoublePanel
        materials={materials}
        position={position}
        rotation={rotate}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    scharnierdeurpaneelpaneelscharnierdeur: (
      <SingleScharnierDoublePanelSingleScharnier
        materials={materials}
        position={position}
        rotation={rotate}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    scharnierdeurscharnierdeurpaneelpaneel: (
      <DoubleScharnierDoublePanelLeft
        materials={materials}
        position={position}
        rotation={rotate}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    schuifpaneelschuiftrap: (
      <SingleSchuifSinglePanelSingleSchuifStairs
        materials={materials}
        position={position}
        rotation={rotate}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
    scharnierpaneelscharniertrap: (
      <SingleScharnierSinglePanelSingleScharnierStairs
        materials={materials}
        position={position}
        rotation={rotate}
        scale={scale}
        doorSettings={doorSettings}
      />
    ),
  };

  const SelectedDoorComponent = doorsRecord[door || ""];
  return SelectedDoorComponent;
};

useGLTF.preload(getFullAssetPath("/objects-v3/materialsCompressedv4.glb"));
export default Door;
